(ns lemonpi.ads-schema.interface
  (:require [clojure.string :as str]
            [lemonpi.ads-schema.facebook :as facebook]
            [lemonpi.ads-schema.tiktok :as tiktok]
            [lemonpi.ads-schema.timezones :as timezones]
            [malli.core :as m]))

(defn url?
  [x]
  (boolean (re-matches #"^(?:https?:)?//.+" x)))

(defn timezone?
  "Validates if x is a valid timezone identifier.
   Uses Java ZoneId validation in Clojure, basic string pattern in ClojureScript."
  [x]
  (when (string? x)
    #?(:clj
       (try
         (java.time.ZoneId/of x)
         true
         (catch Exception _ false))
       :cljs
       ;; Basic validation for ClojureScript - check for common timezone patterns
       ;; This covers most valid timezone formats like "America/New_York", "Europe/Amsterdam", etc.
       (boolean (re-matches #"^[A-Za-z_]+(/[A-Za-z_0-9-]+)*$" x)))))

(def Url
  [:fn {:error/message "Field needs to be a valid URL"} url?])

(def Timezone
  [:fn {:error/message "Timezone must be a valid timezone identifier (e.g., 'America/New_York', 'Europe/Amsterdam')"} timezone?])

(def supported-timezones timezones/supported)

(def ImageUrl
  [:map
   [:url {:error/message "Image url needs to be a valid URL"} Url]])

(def AssetId
  [:map
   [:asset-id {:error/message "Asset id needs to be a valid URL"} string?]])

(def VideoUrl
  [:map
   [:url {:error/message "Video url needs to be a valid URL"} Url]])

(def LemonpiDisplayComponents
  [:map
   [:template {:error/message "Template is required"}
    [:or
     ;; Template with ID (existing template)
     [:map
      [:base-url {:error/message "Template base URL is required"} :string]
      [:id {:error/message "Template ID is required for existing templates"} :int]]
     ;; Template with dimensions (direct template specification)
     [:map
      [:base-url {:error/message "Template base url needs to be a valid URL"} Url]
      [:width {:error/message "Template width is required for direct templates"} nat-int?]
      [:height {:error/message "Template height is required for direct templates"} nat-int?]]]]
   [:content {:error/message "Content placeholders are required"} [:map-of :keyword :any]]
   [:third-party-click-trackers {:optional true} [:sequential :string]] ; These are deprecated
   [:third-party-impression-trackers {:optional true} [:sequential :string]] ; These are deprecated
   [:dynamic-content-config {:optional true} [:map-of :keyword :any]]])

(def TargetingValues
  [:sequential [:map
                [:name {:error/message "Name is required"} :string]
                [:value {:error/message "Value must be an array of strings"} [:sequential :string]]]])

(def Scheduling
  "Schema for ad-variant scheduling. Can be an empty map or a valid object that needs to have at least TZ and start time"
  [:or
   [:map {:closed true}]
   [:map
    [:start {:error/message "Start must be a string"} :string]
    [:end {:optional true :error/message "End must be a string"} [:maybe :string]]
    [:timezone {:error/message "Timezone must be a valid timezone identifier (e.g., 'America/New_York', 'Europe/Amsterdam')"} Timezone]]])

(def meta-cta-options facebook/cta-options)

(def tiktok-cta-options tiktok/cta-options)

(def ExcludeAdBuilderIdAndAdVariantUUID
  [:fn {:error/message "Cannot have both :ad-variant-uuid and :ad-builder-id"}
   (fn [m]
     (not (and (contains? m :ad-variant-uuid)
               (contains? m :ad-builder-id))))])

(def MetaImageAd
  [:and
   [:map
    [:format {:error/message "Format must be 'meta-single-image'"} [:= "meta-single-image"]]
    [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
    [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
    [:content-id {:error/message "Content ID is required"} :string]
    [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
     TargetingValues]
    [:scheduling {:optional true
                  :error/message "Scheduling must be a map with optional end date field"}
     [:maybe Scheduling]]
    [:components
     [:map
      [:image {:error/message "Image is required for Meta image ads"} [:or ImageUrl
                                                                       AssetId]]
      [:primary-text {:error/message "Primary text is required"} :string]
      [:click-through {:error/message "Click through needs to be a valid URL"} Url]
      [:headline :string]
      [:description {:optional true} :string]
      [:cta-caption {:optional true :error/message "CTA caption needs to be a valid URL"} Url]
      [:cta {:error/message "Call to action must be a valid option"}
       (vec (cons :enum facebook/cta-keys))]]]]
   ExcludeAdBuilderIdAndAdVariantUUID])

(def MetaVideoAd
  [:and
   [:map
    {:error/message "Cannot have both :ad-variant-uuid and :ad-builder-id"
     :fn (fn [m]
           (not (and (contains? m :ad-variant-uuid)
                     (contains? m :ad-builder-id))))}
    [:format {:error/message "Format must be 'meta-single-video'"} [:= "meta-single-video"]]
    [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
    [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
    [:content-id {:error/message "Content ID is required"} :string]
    [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
     TargetingValues]
    [:scheduling {:optional true
                  :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
     [:maybe Scheduling]]
    [:components
     [:map
      [:video {:error/message "Video is required for Meta video ads"} [:or VideoUrl AssetId]]
      [:image {:error/message "Image is required for Meta video ads"} [:or VideoUrl AssetId]]
      [:primary-text {:error/message "Primary text is required"} :string]
      [:click-through {:error/message "Click through needs to be a valid URL"} Url]
      [:headline :string]
      [:description {:optional true} :string]
      [:cta-caption {:optional true :error/message "CTA caption needs to be a valid URL"} Url]
      [:cta {:optional true, :error/message "Call to action must be a valid option"}
       (vec (cons :enum facebook/cta-keys))]]]]
   ExcludeAdBuilderIdAndAdVariantUUID])

(def TikTokImageAd
  [:and
   [:map
    [:format {:error/message "Format must be 'tiktok-single-image'"} [:= "tiktok-single-image"]]
    [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
    [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
    [:content-id {:error/message "Content ID is required"} :string]
    [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
     TargetingValues]
    [:scheduling {:optional true
                  :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
     [:maybe Scheduling]]
    [:components
     [:map
      [:image {:error/message "Image is required for TikTok image ads"} [:or ImageUrl AssetId]]
      [:ad-name {:error/message "Ad name is required"} :string]
      [:ad-text {:optional true} :string]
      [:click-through {:error/message "Click through needs to be a valid URL"} Url]
      [:cta {:error/message "Call to action must be a valid option"}
       (vec (cons :enum tiktok/cta-keys))]]]]
   ExcludeAdBuilderIdAndAdVariantUUID])

(def TikTokVideoAd
  [:and
   [:map
    [:format {:error/message "Format must be 'tiktok-single-video'"} [:= "tiktok-single-video"]]
    [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
    [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
    [:content-id {:error/message "Content ID is required"} :string]
    [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
     TargetingValues]
    [:scheduling {:optional true
                  :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
     [:maybe Scheduling]]
    [:components
     [:map
      [:video {:error/message "Video is required for TikTok video ads"} [:or VideoUrl AssetId]]
      [:image {:error/message "Image is required for TikTok image ads"} [:or VideoUrl AssetId]]
      [:ad-name {:error/message "Ad name is required"} :string]
      [:ad-text {:optional true} :string]
      [:click-through {:error/message "Click through needs to be a valid URL"} Url]
      [:cta {:error/message "Call to action must be a valid option"}
       (vec (cons :enum tiktok/cta-keys))]]]]
   ExcludeAdBuilderIdAndAdVariantUUID])

(def LemonpiVastComponents
  [:map
   [:video-1 {:error/message "At least video-1 is required"} VideoUrl]
   [:video-2 {:optional true} VideoUrl]
   [:video-3 {:optional true} VideoUrl]
   [:video-4 {:optional true} VideoUrl]
   [:video-5 {:optional true} VideoUrl]
   [:video-6 {:optional true} VideoUrl]
   [:video-7 {:optional true} VideoUrl]
   [:video-8 {:optional true} VideoUrl]
   [:click-through {:error/message "Click through needs to be a valid URL"} Url]])

(def LemonpiVastTranscodeComponents
  [:map
   [:video VideoUrl]
   [:click-through {:error/message "Click through needs to be a valid URL"} Url]])

(def vast-video-keys (filter #(str/starts-with? (name %) "video-") (map first (rest LemonpiVastComponents))))

(def vast-placements [:enum "web-olv" "amazon-stv"])

(defn vast-transcode-ad
  "A transcoded VAST ad has a single video slot instead of vast-linear's 8, and
   always carries the placement it was transcoded for. Amazon STV ads use this
   shape too, but under their own format so it's visible on the wire."
  [format]
  [:map
   [:format {:error/message (str "Format must be '" format "'")} [:= format]]
   [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
   [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
   [:content-id {:error/message "Human readable ID is required"} :string]
   [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
    TargetingValues]
   [:scheduling {:optional true
                 :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
    [:maybe Scheduling]]
   [:placement vast-placements]
   [:components LemonpiVastTranscodeComponents]])

(def LemonpiVastAd
  (m/schema
   [:and
    [:multi {:dispatch (fn [m] (get m :processing "direct"))
             :error/message "Invalid import method. Must be direct, transcode. Defaults to direct."}
     ["direct"
      [:map
       [:format {:error/message "Format must be 'vast-linear'"} [:= "vast-linear"]]
       [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
       [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
       [:content-id {:error/message "Human readable ID is required"} :string]
       [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
        TargetingValues]
       [:scheduling {:optional true
                     :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
        [:maybe Scheduling]]
       [:placement {:optional true} vast-placements]
       [:components LemonpiVastComponents]]]
     ["transcode" (vast-transcode-ad "vast-linear")]]
    ExcludeAdBuilderIdAndAdVariantUUID]))

(def LemonpiVastAwsPrimeAd
  "Amazon STV ads are transcoded vast-linear ads in every way, except they're
   tagged with their own format so it shows up in reporting."
  (m/schema
   [:and
    (vast-transcode-ad "vast-aws-prime")
    ExcludeAdBuilderIdAndAdVariantUUID]))

(def LemonpiDisplayAd
  [:and
   [:map
    [:format {:error/message "Format must be 'display'"} [:= "display-html"]]
    [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
    [:ad-builder-id {:optional true :error/message "Ad-builder-id must be a valid id"} nat-int?]
    [:content-id {:error/message "Human readable ID is required"} :string]
    [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
     TargetingValues]
    [:scheduling {:optional true
                  :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
     [:maybe Scheduling]]
    [:components {:error/message "Creative components is required for LemonPi display ads"} LemonpiDisplayComponents]]
   ExcludeAdBuilderIdAndAdVariantUUID])

(def MetaCarouselCardMedia
  [:multi {:dispatch :kind
           :error/message "Card media must have kind 'image' or 'video'"}
   ["image" [:map
             [:kind [:= "image"]]
             [:url {:error/message "Card image url needs to be a valid URL"} Url]
             [:width {:optional true} pos-int?]
             [:height {:optional true} pos-int?]
             [:mime-type {:optional true} :string]]]
   ["video" [:map
             [:kind [:= "video"]]
             [:url {:error/message "Card video url needs to be a valid URL"} Url]
             [:thumbnail {:optional true
                          :error/message "Card thumbnail url needs to be a valid URL"} ImageUrl]
             [:width {:optional true} pos-int?]
             [:height {:optional true} pos-int?]
             [:bitrate {:optional true} pos-int?]
             [:duration {:optional true} pos-int?]
             [:mime-type {:optional true} :string]]]])

(def MetaCarouselCard
  [:map
   ;; A single media, or one media per aspect ratio to customize the carousel per
   ;; Meta placement
   [:media {:error/message "Card media is required, either a single media or a list of them"}
    [:multi {:dispatch #(if (sequential? %) :list :single)}
     [:single MetaCarouselCardMedia]
     [:list [:vector {:min 1} MetaCarouselCardMedia]]]]
   [:headline {:error/message "Card headline is required, max 40 characters recommended"}
    :string]
   [:description {:optional true
                  :error/message "Card description, max 20 characters recommended"}
    :string]
   [:link {:error/message "Card link needs to be a valid URL"} Url]
   [:cta {:optional true
          :error/message "Card call to action must be a valid option"}
    (vec (cons :enum facebook/cta-keys))]])

(defn carousel-card-media-list
  "The media of a carousel card, which holds either a single media or one media per
   aspect ratio when the carousel is customized per Meta placement."
  [{:keys [media] :as _card}]
  (if (sequential? media)
    (vec media)
    [media]))

(def MetaCarouselAd
  [:map
   [:format {:error/message "Format must be 'meta-carousel'"} [:= "meta-carousel"]]
   [:ad-variant-uuid {:optional true :error/message "Ad variant uuid must be a valid UUID"} uuid?]
   [:content-id {:error/message "Content ID is required"} :string]
   [:targeting-values {:error/message "Targeting values must be an array of objects with name and value fields"}
    TargetingValues]
   [:scheduling {:optional true
                 :error/message "Scheduling must be a map with optional start, end, and timezone fields"}
    [:maybe Scheduling]]
   [:components
    [:map
     [:primary-text {:error/message "Primary text is required"} :string]
     [:cta {:error/message "Call to action must be a valid option"}
      (vec (cons :enum facebook/cta-keys))]
     [:cards {:error/message "Cards are required for carousel ads, minimum 2 maximum 10"}
      [:vector {:min 2 :max 10} MetaCarouselCard]]]]])

(def Ad

  [:multi {:dispatch :format
           :error/message "Invalid ad type. Must be meta-single-image, meta-single-video, tiktok-single-image, tiktok-single-video, vast-linear, vast-aws-prime, display-html, or meta-carousel"}
   ["meta-single-image" MetaImageAd]
   ["meta-single-video" MetaVideoAd]
   ["tiktok-single-image" TikTokImageAd]
   ["tiktok-single-video" TikTokVideoAd]
   ["vast-linear" LemonpiVastAd]
   ["vast-aws-prime" LemonpiVastAwsPrimeAd]
   ["display-html" LemonpiDisplayAd]
   ["meta-carousel" MetaCarouselAd]])
